import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, Spinner } from '@rankpulse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import {
	Activity,
	BookOpen,
	CalendarClock,
	Check,
	Compass,
	Gauge,
	Globe2,
	LineChart,
	Map as MapIcon,
	MapPin,
	MousePointerClick,
	Plus,
	Radar,
	Search,
	Sparkles,
	Users,
	X,
} from 'lucide-react';
import { useState } from 'react';
import { AddCompetitorDrawer } from '../components/add-competitor-drawer.js';
import { AddDomainDrawer } from '../components/add-domain-drawer.js';
import { AddLocationDrawer } from '../components/add-location-drawer.js';
import { AddRadarDomainDrawer } from '../components/add-radar-domain-drawer.js';
import { AiAlertsPanel } from '../components/ai-alerts-panel.js';
import { AiPresenceCard } from '../components/ai-presence-card.js';
import { AppShell } from '../components/app-shell.js';
import { DailyActionsCard } from '../components/daily-actions-card.js';
import { ImportKeywordsDrawer } from '../components/import-keywords-drawer.js';
import { LinkBingDrawer } from '../components/link-bing-drawer.js';
import { LinkClarityDrawer } from '../components/link-clarity-drawer.js';
import { LinkGa4Drawer } from '../components/link-ga4-drawer.js';
import { LinkWikipediaDrawer } from '../components/link-wikipedia-drawer.js';
import { ProjectKpiGrid } from '../components/project-kpi-grid.js';
import { TrackPageDrawer } from '../components/track-page-drawer.js';
import { api } from '../lib/api.js';

export const ProjectDetailPage = () => {
	const { id } = useParams({ from: '/projects/$id' });
	const [openDrawer, setOpenDrawer] = useState<
		| 'competitor'
		| 'keywords'
		| 'domain'
		| 'location'
		| 'wikipedia'
		| 'page-speed'
		| 'ga4'
		| 'bing'
		| 'radar'
		| 'clarity'
		| null
	>(null);

	const projectQuery = useQuery({
		queryKey: ['project', id],
		queryFn: () => api.projects.get(id),
	});

	const competitorsQuery = useQuery({
		queryKey: ['project', id, 'competitors'],
		queryFn: () => api.projects.listCompetitors(id),
		enabled: Boolean(projectQuery.data),
	});

	const keywordsQuery = useQuery({
		queryKey: ['project', id, 'keywords'],
		queryFn: () => api.projects.listKeywordLists(id),
		enabled: Boolean(projectQuery.data),
	});

	const suggestionsQuery = useQuery({
		queryKey: ['project', id, 'competitor-suggestions'],
		queryFn: () => api.projects.listCompetitorSuggestions(id),
		enabled: Boolean(projectQuery.data),
	});

	const wikipediaQuery = useQuery({
		queryKey: ['project', id, 'wikipedia'],
		queryFn: () => api.wikipedia.listForProject(id),
		enabled: Boolean(projectQuery.data),
	});

	const pageSpeedQuery = useQuery({
		queryKey: ['project', id, 'page-speed'],
		queryFn: () => api.pageSpeed.listForProject(id),
		enabled: Boolean(projectQuery.data),
	});

	const ga4Query = useQuery({
		queryKey: ['project', id, 'ga4'],
		queryFn: () => api.ga4.listForProject(id),
		enabled: Boolean(projectQuery.data),
	});

	const bingQuery = useQuery({
		queryKey: ['project', id, 'bing'],
		queryFn: () => api.bing.listForProject(id),
		enabled: Boolean(projectQuery.data),
	});

	const radarQuery = useQuery({
		queryKey: ['project', id, 'radar'],
		queryFn: () => api.radar.listForProject(id),
		enabled: Boolean(projectQuery.data),
	});

	const clarityQuery = useQuery({
		queryKey: ['project', id, 'clarity'],
		queryFn: () => api.clarity.listForProject(id),
		enabled: Boolean(projectQuery.data),
	});

	const qc = useQueryClient();
	const promoteSuggestion = useMutation({
		mutationFn: (suggestionId: string) => api.projects.promoteCompetitorSuggestion(suggestionId),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['project', id, 'competitor-suggestions'] });
			qc.invalidateQueries({ queryKey: ['project', id, 'competitors'] });
		},
	});
	const dismissSuggestion = useMutation({
		mutationFn: (suggestionId: string) => api.projects.dismissCompetitorSuggestion(suggestionId),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['project', id, 'competitor-suggestions'] });
		},
	});

	if (projectQuery.isLoading) {
		return (
			<AppShell>
				<div className="flex justify-center py-10">
					<Spinner size="lg" />
				</div>
			</AppShell>
		);
	}

	if (projectQuery.isError || !projectQuery.data) {
		return (
			<AppShell>
				<p className="px-4 py-8 text-sm text-destructive sm:px-6">
					{projectQuery.error instanceof Error ? projectQuery.error.message : 'Project not found'}
				</p>
			</AppShell>
		);
	}

	const project = projectQuery.data;
	return (
		<AppShell>
			<div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
				<header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
					<div>
						<h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{project.name}</h1>
						<p className="text-sm text-muted-foreground">{project.primaryDomain}</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<Link to="/projects/$id/cockpit" params={{ id: project.id }}>
							<Button variant="primary" size="sm">
								<Compass size={14} />
								Cockpit
							</Button>
						</Link>
						<Link to="/projects/$id/rankings" params={{ id: project.id }}>
							<Button variant="secondary" size="sm">
								<LineChart size={14} />
								Rankings
							</Button>
						</Link>
						<Link to="/projects/$id/serp-map" params={{ id: project.id }}>
							<Button variant="secondary" size="sm">
								<MapIcon size={14} />
								SERP Map
							</Button>
						</Link>
						<Link to="/projects/$id/competitors" params={{ id: project.id }}>
							<Button variant="secondary" size="sm">
								<Users size={14} />
								Competitors
							</Button>
						</Link>
						<Link to="/projects/$id/competitor-intelligence" params={{ id: project.id }}>
							<Button variant="secondary" size="sm">
								<Users size={14} />
								Competitor Intelligence
							</Button>
						</Link>
						<Link to="/projects/$id/schedules" params={{ id: project.id }}>
							<Button variant="secondary" size="sm">
								<CalendarClock size={14} />
								Schedules
							</Button>
						</Link>
						<Link to="/projects/$id/gsc" params={{ id: project.id }}>
							<Button variant="secondary" size="sm">
								<Search size={14} />
								GSC
							</Button>
						</Link>
						<Link to="/projects/$id/ga4" params={{ id: project.id }}>
							<Button variant="secondary" size="sm">
								<Activity size={14} />
								GA4
							</Button>
						</Link>
						<Link to="/projects/$id/ai-radar" params={{ id: project.id }}>
							<Button variant="secondary" size="sm">
								<Sparkles size={14} />
								AI Radar
							</Button>
						</Link>
						<Badge
							variant={
								project.kind === 'OWN' ? 'default' : project.kind === 'COMPETITOR' ? 'warning' : 'secondary'
							}
						>
							{project.kind.toLowerCase()}
						</Badge>
					</div>
				</header>

				<AiPresenceCard projectId={project.id} />
				<AiAlertsPanel projectId={project.id} />

				<ProjectKpiGrid project={project} />

				<DailyActionsCard project={project} />

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="text-base">Domains ({project.domains.length})</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('domain')}>
								<Plus size={14} />
								Add
							</Button>
						</CardHeader>
						<CardContent>
							<ul className="space-y-1 text-sm">
								{project.domains.map((d) => (
									<li key={d.domain} className="flex items-center justify-between gap-2">
										<span className="break-all">{d.domain}</span>
										<Badge variant="secondary">{d.kind}</Badge>
									</li>
								))}
							</ul>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="text-base">Locations ({project.locations.length})</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('location')}>
								<MapPin size={14} />
								Add
							</Button>
						</CardHeader>
						<CardContent>
							{project.locations.length === 0 ? (
								<p className="text-sm text-muted-foreground">No locations targeted yet.</p>
							) : (
								<ul className="space-y-1 text-sm">
									{project.locations.map((l) => (
										<li key={`${l.country}-${l.language}`}>
											{l.language} · {l.country}
										</li>
									))}
								</ul>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="text-base">
								Competitors ({competitorsQuery.data?.length ?? '…'})
							</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('competitor')}>
								<Plus size={14} />
								Add
							</Button>
						</CardHeader>
						<CardContent>
							{competitorsQuery.isLoading ? (
								<Spinner />
							) : competitorsQuery.data && competitorsQuery.data.length > 0 ? (
								<ul className="space-y-1 text-sm">
									{competitorsQuery.data.map((c) => (
										<li key={c.id} className="break-words">
											<span className="font-medium">{c.label}</span>{' '}
											<span className="text-muted-foreground">· {c.domain}</span>
										</li>
									))}
								</ul>
							) : (
								<EmptyState
									title="No competitors tracked"
									description="Add a competing domain to compare rankings side-by-side."
									action={
										<Button size="sm" onClick={() => setOpenDrawer('competitor')}>
											<Plus size={14} />
											Add competitor
										</Button>
									}
								/>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="flex items-center gap-2 text-base">
								<Sparkles size={14} className="text-primary" />
								Suggested competitors
								{suggestionsQuery.data && suggestionsQuery.data.length > 0 && (
									<Badge variant="default">{suggestionsQuery.data.length}</Badge>
								)}
							</CardTitle>
						</CardHeader>
						<CardContent>
							{suggestionsQuery.isLoading ? (
								<Spinner />
							) : suggestionsQuery.data && suggestionsQuery.data.length > 0 ? (
								<ul className="space-y-2 text-sm">
									{suggestionsQuery.data.map((s) => (
										<li
											key={s.id}
											className="flex flex-col gap-2 rounded border border-border p-2 sm:flex-row sm:items-center sm:justify-between"
										>
											<div className="min-w-0 flex-1">
												<p className="break-words font-medium">{s.domain}</p>
												<p className="text-xs text-muted-foreground">
													{s.distinctKeywordsInTop10} keywords · {s.totalTop10Hits} top-10 hits
												</p>
											</div>
											<div className="flex gap-1">
												<Button
													size="sm"
													variant="ghost"
													disabled={promoteSuggestion.isPending}
													onClick={() => promoteSuggestion.mutate(s.id)}
													aria-label={`Promote ${s.domain}`}
												>
													<Check size={14} />
													Promote
												</Button>
												<Button
													size="sm"
													variant="ghost"
													disabled={dismissSuggestion.isPending}
													onClick={() => dismissSuggestion.mutate(s.id)}
													aria-label={`Dismiss ${s.domain}`}
												>
													<X size={14} />
													Dismiss
												</Button>
											</div>
										</li>
									))}
								</ul>
							) : (
								<p className="text-sm text-muted-foreground">
									No suggestions yet. Once SERP fetches run for this project, frequent top-10 domains will
									show up here.
								</p>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="flex items-center gap-2 text-base">
								<Gauge size={14} className="text-primary" />
								PageSpeed pages ({pageSpeedQuery.data?.length ?? '…'})
							</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('page-speed')}>
								<Plus size={14} />
								Track
							</Button>
						</CardHeader>
						<CardContent>
							{pageSpeedQuery.isLoading ? (
								<Spinner />
							) : pageSpeedQuery.data && pageSpeedQuery.data.length > 0 ? (
								<ul className="space-y-1 text-sm">
									{pageSpeedQuery.data.map((p) => (
										<li key={p.id} className="break-words">
											<Badge variant={p.strategy === 'mobile' ? 'default' : 'secondary'}>{p.strategy}</Badge>{' '}
											<span className="text-muted-foreground">{p.url}</span>
										</li>
									))}
								</ul>
							) : (
								<EmptyState
									title="No pages tracked yet"
									description="Track a URL (mobile or desktop) to monitor LCP / INP / CLS over time."
									action={
										<Button size="sm" onClick={() => setOpenDrawer('page-speed')}>
											<Plus size={14} />
											Track page
										</Button>
									}
								/>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="flex items-center gap-2 text-base">
								<Radar size={14} className="text-primary" />
								Radar domains ({radarQuery.data?.length ?? '…'})
							</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('radar')}>
								<Plus size={14} />
								Monitor
							</Button>
						</CardHeader>
						<CardContent>
							{radarQuery.isLoading ? (
								<Spinner />
							) : radarQuery.data && radarQuery.data.length > 0 ? (
								<ul className="space-y-1 text-sm">
									{radarQuery.data.map((d) => (
										<li key={d.id} className="break-words">
											<Badge variant={d.isActive ? 'default' : 'secondary'}>
												{d.isActive ? 'active' : 'removed'}
											</Badge>{' '}
											<span className="text-muted-foreground">{d.domain}</span>
										</li>
									))}
								</ul>
							) : (
								<EmptyState
									title="No domain monitored"
									description="Track a domain's global popularity ranking from Cloudflare Radar — useful as a macro-trend signal beside organic search performance."
									action={
										<Button size="sm" onClick={() => setOpenDrawer('radar')}>
											<Plus size={14} />
											Monitor domain
										</Button>
									}
								/>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="flex items-center gap-2 text-base">
								<MousePointerClick size={14} className="text-primary" />
								Clarity ({clarityQuery.data?.length ?? '…'})
							</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('clarity')}>
								<Plus size={14} />
								Link
							</Button>
						</CardHeader>
						<CardContent>
							{clarityQuery.isLoading ? (
								<Spinner />
							) : clarityQuery.data && clarityQuery.data.length > 0 ? (
								<ul className="space-y-1 text-sm">
									{clarityQuery.data.map((c) => (
										<li key={c.id} className="break-words">
											<Badge variant={c.isActive ? 'default' : 'secondary'}>
												{c.isActive ? 'active' : 'unlinked'}
											</Badge>{' '}
											<span className="text-muted-foreground">{c.clarityHandle}</span>
										</li>
									))}
								</ul>
							) : (
								<EmptyState
									title="No Clarity project linked"
									description="Link a Microsoft Clarity project to track sessions, rage clicks, dead clicks, scroll depth, and engagement time as UX behavioural signals."
									action={
										<Button size="sm" onClick={() => setOpenDrawer('clarity')}>
											<Plus size={14} />
											Link project
										</Button>
									}
								/>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="flex items-center gap-2 text-base">
								<Globe2 size={14} className="text-primary" />
								Bing properties ({bingQuery.data?.length ?? '…'})
							</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('bing')}>
								<Plus size={14} />
								Link
							</Button>
						</CardHeader>
						<CardContent>
							{bingQuery.isLoading ? (
								<Spinner />
							) : bingQuery.data && bingQuery.data.length > 0 ? (
								<ul className="space-y-1 text-sm">
									{bingQuery.data.map((p) => (
										<li key={p.id} className="break-words">
											<Badge variant={p.isActive ? 'default' : 'secondary'}>
												{p.isActive ? 'active' : 'unlinked'}
											</Badge>{' '}
											<span className="text-muted-foreground">{p.siteUrl}</span>
										</li>
									))}
								</ul>
							) : (
								<EmptyState
									title="No Bing property linked"
									description="Link a Bing-verified property to track clicks, impressions, and average position from the Bing search engine."
									action={
										<Button size="sm" onClick={() => setOpenDrawer('bing')}>
											<Plus size={14} />
											Link property
										</Button>
									}
								/>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="flex items-center gap-2 text-base">
								<Activity size={14} className="text-primary" />
								GA4 properties ({ga4Query.data?.length ?? '…'})
							</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('ga4')}>
								<Plus size={14} />
								Link
							</Button>
						</CardHeader>
						<CardContent>
							{ga4Query.isLoading ? (
								<Spinner />
							) : ga4Query.data && ga4Query.data.length > 0 ? (
								<ul className="space-y-1 text-sm">
									{ga4Query.data.map((p) => (
										<li key={p.id} className="break-words">
											<Badge variant={p.isActive ? 'default' : 'secondary'}>
												{p.isActive ? 'active' : 'unlinked'}
											</Badge>{' '}
											<span className="font-medium">properties/{p.propertyHandle}</span>
										</li>
									))}
								</ul>
							) : (
								<EmptyState
									title="No GA4 property linked"
									description="Link a property to pull sessions, users, and pageviews from your real GA4 traffic."
									action={
										<Button size="sm" onClick={() => setOpenDrawer('ga4')}>
											<Plus size={14} />
											Link property
										</Button>
									}
								/>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="flex items-center gap-2 text-base">
								<BookOpen size={14} className="text-primary" />
								Wikipedia entities ({wikipediaQuery.data?.length ?? '…'})
							</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('wikipedia')}>
								<Plus size={14} />
								Link
							</Button>
						</CardHeader>
						<CardContent>
							{wikipediaQuery.isLoading ? (
								<Spinner />
							) : wikipediaQuery.data && wikipediaQuery.data.length > 0 ? (
								<ul className="space-y-1 text-sm">
									{wikipediaQuery.data.map((a) => (
										<li key={a.id} className="break-words">
											<span className="font-medium">{a.label}</span>{' '}
											<span className="text-muted-foreground">
												· {a.slug} on {a.wikipediaProject}
											</span>
										</li>
									))}
								</ul>
							) : (
								<EmptyState
									title="No Wikipedia entities tracked"
									description="Link an article (yours, a competitor's, or an industry topic) to monitor pageviews as a brand-awareness signal."
									action={
										<Button size="sm" onClick={() => setOpenDrawer('wikipedia')}>
											<Plus size={14} />
											Link article
										</Button>
									}
								/>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between gap-2">
							<CardTitle className="text-base">
								Keyword lists ({keywordsQuery.data?.reduce((acc, l) => acc + l.keywords.length, 0) ?? '…'})
							</CardTitle>
							<Button variant="ghost" size="sm" onClick={() => setOpenDrawer('keywords')}>
								<Plus size={14} />
								Import
							</Button>
						</CardHeader>
						<CardContent>
							{keywordsQuery.isLoading ? (
								<Spinner />
							) : keywordsQuery.data && keywordsQuery.data.length > 0 ? (
								<ul className="space-y-2 text-sm">
									{keywordsQuery.data.map((list) => (
										<li key={list.id}>
											<p className="font-medium">{list.name}</p>
											<p className="text-xs text-muted-foreground">{list.keywords.length} keywords</p>
										</li>
									))}
								</ul>
							) : (
								<EmptyState
									title="No keywords yet"
									description="Bulk-import a list of phrases to start tracking."
									action={
										<Button size="sm" onClick={() => setOpenDrawer('keywords')}>
											<Plus size={14} />
											Import keywords
										</Button>
									}
								/>
							)}
						</CardContent>
					</Card>
				</div>
			</div>

			<AddCompetitorDrawer
				projectId={id}
				open={openDrawer === 'competitor'}
				onClose={() => setOpenDrawer(null)}
			/>
			<ImportKeywordsDrawer
				projectId={id}
				open={openDrawer === 'keywords'}
				onClose={() => setOpenDrawer(null)}
			/>
			<AddDomainDrawer projectId={id} open={openDrawer === 'domain'} onClose={() => setOpenDrawer(null)} />
			<AddLocationDrawer
				projectId={id}
				open={openDrawer === 'location'}
				onClose={() => setOpenDrawer(null)}
			/>
			<LinkWikipediaDrawer
				projectId={id}
				open={openDrawer === 'wikipedia'}
				onClose={() => setOpenDrawer(null)}
			/>
			<TrackPageDrawer
				projectId={id}
				open={openDrawer === 'page-speed'}
				onClose={() => setOpenDrawer(null)}
			/>
			<LinkGa4Drawer projectId={id} open={openDrawer === 'ga4'} onClose={() => setOpenDrawer(null)} />
			<LinkBingDrawer projectId={id} open={openDrawer === 'bing'} onClose={() => setOpenDrawer(null)} />
			<AddRadarDomainDrawer
				projectId={id}
				open={openDrawer === 'radar'}
				onClose={() => setOpenDrawer(null)}
			/>
			<LinkClarityDrawer projectId={id} open={openDrawer === 'clarity'} onClose={() => setOpenDrawer(null)} />
		</AppShell>
	);
};
